import { postAction, isOffline } from '../api/client';
import type { Snapshot, TagResolveOk } from '../api/types';
import { saveSnapshot, loadSnapshot, kvGet, kvSet } from './idb';
import { listOutbox } from './outbox';
import { evaluateSchedule, DEFAULT_SCHEDULE_CONFIG, type ScheduleConfig } from '../lib/schedule';
import { capturedNowIso } from '../lib/serverTime';

const REFRESH_MIN_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Synchronous in-memory copy of the last snapshot, so the scan → form path can
 * paint instantly instead of waiting on IndexedDB or the network. Primed at app
 * start and kept current by refreshSnapshot().
 */
let mem: Snapshot | undefined;
export function snapshotSync(): Snapshot | undefined { return mem; }
export async function primeSnapshot(): Promise<void> {
  try { mem = await loadSnapshot(); } catch { /* ignore */ }
}

/**
 * Fold a just-submitted reading into the in-memory snapshot so an immediate
 * re-scan of the same equipment sees the correct (blocked) schedule before the
 * next full snapshot refresh.
 */
export function noteLocalReading(equipmentId: string, values: Record<string, number | string>): void {
  if (!mem) return;
  const e = mem.equipment.find((x) => x.equipmentId === equipmentId);
  if (!e) return;
  e.lastReadingAt = capturedNowIso();
  e.lastValues = { ...(e.lastValues ?? {}), ...values };
  void saveSnapshot(mem).catch(() => {});
}

/**
 * Resolve a tag from the in-memory snapshot alone — no await, no network.
 * Used as the instant seed for the reading screens; the authoritative `tag`
 * call still runs and reconciles. Returns null if the snapshot hasn't loaded
 * yet or doesn't know this tag.
 */
export function resolveTagLocal(code: string): TagResolveOk | null {
  const snap = mem;
  if (!snap) return null;
  const tag = snap.tags.find((t) => t.code.toUpperCase() === code.toUpperCase());
  if (!tag) return null;
  const equip = snap.equipment.find((e) => e.equipmentId === tag.equipmentId);
  if (!equip) return null;
  const template = snap.templates.find((t) => t.templateId === equip.templateId);
  if (!template) return null;

  const cfg: ScheduleConfig = { ...DEFAULT_SCHEDULE_CONFIG, ...(equip.schedule || {}) };
  const lastAt = equip.lastReadingAt ?? null;
  const now = new Date(capturedNowIso());
  const schedule = evaluateSchedule(now, lastAt ? new Date(lastAt) : null, cfg);

  return {
    ok: true,
    now: now.toISOString(),
    tag: { code },
    equipment: { equipmentId: equip.equipmentId, name: equip.name, location: equip.location, templateId: equip.templateId },
    template,
    schedule,
    lastReading: lastAt
      ? { submissionId: '', effectiveReadingAt: lastAt, operatorName: '', abnormalStatus: 'normal', source: 'online' }
      : null,
    lastValues: equip.lastValues ?? {},
  };
}

/**
 * Refresh the offline snapshot from the backend, but at most once every 15 min
 * (unless force=true) so opening the app repeatedly doesn't hammer Apps Script.
 */
export async function refreshSnapshot(force = false): Promise<Snapshot | undefined> {
  if (isOffline()) return loadSnapshot();
  if (!force) {
    const last = (await kvGet<number>('snapshotFetchedAt')) ?? 0;
    if (Date.now() - last < REFRESH_MIN_INTERVAL_MS) return loadSnapshot();
  }
  try {
    const s = await postAction<Snapshot>('bootstrap', {}, { timeoutMs: 25000 });
    if (s && s.ok) {
      await saveSnapshot(s);
      await kvSet('snapshotFetchedAt', Date.now());
      mem = s;
      return s;
    }
  } catch {
    /* offline or server slow — keep whatever we have */
  }
  const fallback = await loadSnapshot();
  if (fallback) mem = fallback;
  return fallback;
}

export async function getSnapshot(): Promise<Snapshot | undefined> {
  return loadSnapshot();
}

/**
 * Resolve a tag using only cached data + the local outbox. Used when the network
 * call fails. Returns null if the tag has never been seen online.
 */
export async function resolveTagOffline(code: string): Promise<TagResolveOk | null> {
  const snap = await loadSnapshot();
  if (!snap) return null;
  const tag = snap.tags.find((t) => t.code.toUpperCase() === code.toUpperCase());
  if (!tag) return null;
  const equip = snap.equipment.find((e) => e.equipmentId === tag.equipmentId);
  if (!equip) return null;
  const template = snap.templates.find((t) => t.templateId === equip.templateId);
  if (!template) return null;

  // last reading = latest of (nothing we cache from server) or local queued items for this equipment
  const mine = (await listOutbox())
    .filter((i) => i.equipmentName === equip.name && (i.status === 'pending' || i.status === 'synced' || i.status === 'syncing'))
    .sort((a, b) => b.clientCapturedAt.localeCompare(a.clientCapturedAt));
  const lastAt = mine[0]?.clientCapturedAt ?? null;

  const cfg: ScheduleConfig = { ...DEFAULT_SCHEDULE_CONFIG, ...(equip.schedule || {}) };
  const now = new Date(capturedNowIso());
  const schedule = evaluateSchedule(now, lastAt ? new Date(lastAt) : null, cfg);

  return {
    ok: true,
    now: now.toISOString(),
    tag: { code },
    equipment: { equipmentId: equip.equipmentId, name: equip.name, location: equip.location, templateId: equip.templateId },
    template,
    schedule,
    lastReading: lastAt
      ? { submissionId: mine[0].submissionId, effectiveReadingAt: lastAt, operatorName: mine[0].operatorName, abnormalStatus: 'normal', source: 'offline' }
      : null,
    lastValues: mine[0]?.values ?? {},
  };
}
